import { AIModel } from '@/types';

export const LOCAL_MODEL_ICON_FILENAMES = [
  'claude.svg',
  'cohere.svg',
  'deepseek.svg',
  'gemini.svg',
  'gemma.svg',
  'local-model.svg',
  'maplept.svg',
  'meta.svg',
  'openai.svg',
  'phi.svg',
  'qwen.svg',
  'smolllm.png',
  'stability.svg',
] as const;

export const LOCAL_MODEL_ICON_OPTIONS = [
  { filename: 'claude.svg', label: 'Claude' },
  { filename: 'cohere.svg', label: 'Cohere' },
  { filename: 'deepseek.svg', label: 'DeepSeek' },
  { filename: 'gemini.svg', label: 'Gemini' },
  { filename: 'gemma.svg', label: 'Gemma' },
  { filename: 'local-model.svg', label: 'Generic local model' },
  { filename: 'maplept.svg', label: 'MaplePT' },
  { filename: 'meta.svg', label: 'Meta / Llama' },
  { filename: 'openai.svg', label: 'OpenAI / GPT' },
  { filename: 'phi.svg', label: 'Phi' },
  { filename: 'qwen.svg', label: 'Qwen' },
  { filename: 'smolllm.png', label: 'SmolLM' },
  { filename: 'stability.svg', label: 'Stability / StableLM' },
] as const;

const SAFE_ICON_FILENAME = /^[a-z0-9][a-z0-9._-]*$/i;

const getAssetBasePath = () => {
  const baseUrl = import.meta.env.BASE_URL || '/';
  return baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
};

export function getModelIconPath(iconFilename?: string | null) {
  if (!iconFilename) return undefined;

  const trimmed = iconFilename.trim();
  if (!trimmed || trimmed.includes('/') || trimmed.includes('\\') || !SAFE_ICON_FILENAME.test(trimmed)) {
    return undefined;
  }

  return `${getAssetBasePath()}images/models/${trimmed}`;
}

export function inferModelIconFilename(model: Partial<Pick<AIModel, 'provider' | 'name' | 'family' | 'apiName' | 'logo_url' | 'icon_filename'>>) {
  if (model.icon_filename) {
    return model.icon_filename;
  }

  const haystack = [model.provider, model.name, model.family, model.apiName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!haystack) return undefined;
  if (haystack.includes('claude')) return 'claude.svg';
  if (haystack.includes('openai') || haystack.includes('gpt-')) return 'openai.svg';
  if (haystack.includes('cohere') || haystack.includes('command a')) return 'cohere.svg';
  if (haystack.includes('gemini')) return 'gemini.svg';
  if (haystack.includes('deepseek')) return 'deepseek.svg';
  if (haystack.includes('maplept')) return 'maplept.svg';
  if (haystack.includes('gemma')) return 'gemma.svg';
  if (haystack.includes('qwen')) return 'qwen.svg';
  if (haystack.includes('phi')) return 'phi.svg';
  if (haystack.includes('smollm')) return 'smolllm.png';
  if (haystack.includes('stablelm') || haystack.includes('stability')) return 'stability.svg';
  if (haystack.includes('llama') || haystack.includes('meta') || haystack.includes('hermes')) return 'meta.svg';
  return 'local-model.svg';
}

export function resolveModelIconSrc(model: Partial<Pick<AIModel, 'provider' | 'name' | 'family' | 'apiName' | 'logo_url' | 'icon_filename'>>) {
  const localIcon = getModelIconPath(model.icon_filename) || getModelIconPath(inferModelIconFilename(model));
  if (localIcon) {
    return localIcon;
  }

  if (typeof model.logo_url === 'string' && model.logo_url.trim()) {
    return model.logo_url;
  }

  return undefined;
}